import { useEffect, useRef } from "react"

const IntercomMessenger = ({ user, sessionDuration = 86400000 }) => {
  const intercomRef = useRef(null)
  const observerRef = useRef(null)

  useEffect(() => {
    // Only initialize Intercom if we have a user
    if (!user) {
      return
    }

    const hasPractice = !!(user.practice_id || user.practice?.id || user.practice)

    // Function to force left positioning on Intercom elements
    const forceLeftPosition = () => {
      const leftPosition = window.innerWidth <= 768 ? "10px" : "20px"
      const bottomPosition = window.innerWidth <= 768 ? "10px" : "20px"

      // Get all Intercom-related elements
      const selectors = [
        ".intercom-launcher",
        ".intercom-messenger",
        ".intercom-messenger-frame",
        "#intercom-container",
        'iframe[name^="intercom"]',
        'iframe[id^="intercom"]'
      ]

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector)
        elements.forEach((element) => {
          if (element) {
            element.style.setProperty("left", leftPosition, "important")
            element.style.setProperty("right", "auto", "important")
            if (selector.includes("launcher") || selector.includes("messenger")) {
              element.style.setProperty("bottom", bottomPosition, "important")
            }
          }
        })
      })
    }

    // Setup MutationObserver to watch for Intercom DOM changes
    const setupObserver = () => {
      // Disconnect existing observer if any
      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      // Create a new observer
      observerRef.current = new MutationObserver((mutations) => {
        // Check if any Intercom-related elements were modified
        let shouldReposition = false
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes" && mutation.attributeName === "style") {
            const target = mutation.target
            const className = typeof target.className === "string" ? target.className : String(target.className || "")
            if (className.includes("intercom") || target.id === "intercom-container") {
              shouldReposition = true
            }
          } else if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                const className = typeof node.className === "string" ? node.className : String(node.className || "")
                if (
                  className.includes("intercom") ||
                  node.id === "intercom-container" ||
                  (node.tagName === "IFRAME" && (node.name?.startsWith("intercom") || node.id?.startsWith("intercom")))
                ) {
                  shouldReposition = true
                }
              }
            })
          }
        })

        if (shouldReposition) {
          forceLeftPosition()
        }
      })

      // Start observing the document body
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"]
      })
    }

    const initializeIntercom = async () => {
      // First, shutdown any existing Intercom session to prevent conflicts
      if (window.Intercom) {
        window.Intercom("shutdown")
      }
      // Drop legacy identity-verification settings so boot() never sends user_hash with JWT
      try {
        delete window.intercomSettings
      } catch (_e) {
        window.intercomSettings = undefined
      }
      try {
        // Fetch JWT token from our backend
        const response = await fetch("/intercom/token", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
          },
          credentials: "same-origin"
        })

        if (!response.ok) {
          console.error("Failed to fetch Intercom JWT token")
          return
        }

        const data = await response.json()

        if (!data.success || !data.token) {
          console.error("Invalid response from Intercom token endpoint")
          return
        }

        // Initialize Intercom with JWT authentication
        window.Intercom("boot", {
          app_id: data.app_id,
          intercom_user_jwt: data.token,
          session_duration: sessionDuration,
          // You can add additional non-sensitive attributes here
          // These will be sent outside the JWT and can be updated by the user
          user_type: user.practitioner ? "practitioner" : "consumer",
          has_practice: hasPractice,
          // Add any other non-sensitive data you want to track
          // Custom positioning (CSS will override this, but good to have as fallback)
          horizontal_padding: 20,
          vertical_padding: 20,
          alignment: "left", // Try to set alignment to left

          // Be explicit: ensure the launcher is visible.
          // We historically configured Intercom for a custom activator; if that activator isn't present
          // (or a workspace setting drifted), some users can end up with no obvious entry point.
          hide_default_launcher: false,

          // If we do provide a custom activator (e.g. a "Help" link), Intercom can hook it up.
          custom_launcher_selector: "#Intercom"
        })

        // Store reference for cleanup
        intercomRef.current = window.Intercom

        // Guard against any later updates that might hide the launcher.
        window.Intercom("update", { hide_default_launcher: false })

        // Setup observer after Intercom is initialized
        setTimeout(() => {
          setupObserver()
          // Force initial positioning
          forceLeftPosition()
        }, 500)

        // Also force positioning when Intercom events occur
        window.Intercom("onShow", forceLeftPosition)
        window.Intercom("onHide", forceLeftPosition)
      } catch (error) {
        console.error("Error initializing Intercom:", error)
      }
    }

    initializeIntercom()

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      if (intercomRef.current) {
        window.Intercom("shutdown")
      }
    }
  }, [user, sessionDuration])

  // Component doesn't render anything visible
  return null
}

export default IntercomMessenger
